import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable, { Column } from "@/components/ui/DataTable";
import { useGetSessionsQuery } from "@/redux/services/pos/registerSession.api";
import { useRestaurantCurrency } from "@/lib/hooks";
import {
  displayPrice,
  formatDate,
  formatVariance,
  getSessionStatusLabel,
} from "@/lib/utils";
import { RegisterSession } from "@/redux/services/pos/pos.type";

interface SessionsListProps {
  restaurantId: string;
}

const SessionsList = ({ restaurantId }: SessionsListProps) => {
  const navigate = useNavigate();
  const currency = useRestaurantCurrency();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isFetching } = useGetSessionsQuery({
    restaurantId,
    query: { page, limit: pageSize },
  });

  const sessions = data?.results ?? [];

  const varianceClass = (variance: number | undefined) => {
    if (variance == null) return "";
    if (variance > 0) return "text-green-600";
    if (variance < 0) return "text-red-600";
    return "text-muted-foreground";
  };

  const columns: Column[] = [
    {
      key: "openedAt",
      label: "Ouverture",
      formatter: (v: string) => formatDate(new Date(v), true, true),
    },
    {
      key: "closedAt",
      label: "Fermeture",
      formatter: (v: string | undefined) =>
        v ? (
          formatDate(new Date(v), true, true)
        ) : (
          <span className="text-muted-foreground">En cours</span>
        ),
    },
    {
      key: "openingBalance",
      label: "Fond de caisse",
      formatter: (v: number) => (
        <span className="tabular-nums">{displayPrice(v, currency)}</span>
      ),
    },
    {
      key: "expectedCash",
      label: "Espèces attendues",
      formatter: (v: number | undefined) =>
        v != null ? (
          <span className="tabular-nums">{displayPrice(v, currency)}</span>
        ) : (
          "—"
        ),
    },
    {
      key: "actualCash",
      label: "Espèces comptées",
      formatter: (v: number | undefined) =>
        v != null ? (
          <span className="tabular-nums">{displayPrice(v, currency)}</span>
        ) : (
          "—"
        ),
    },
    {
      key: "variance",
      label: "Écart",
      formatter: (v: number | undefined) => (
        <span className={`tabular-nums ${varianceClass(v)}`}>
          {v != null ? formatVariance(v / 100, currency) : "—"}
        </span>
      ),
    },
    {
      key: "status",
      label: "Statut",
      formatter: (v: RegisterSession["status"]) => (
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
            v === "open"
              ? "bg-green-100 text-green-700"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {getSessionStatusLabel(v)}
        </span>
      ),
    },
  ];

  return (
    <DataTable
      data={sessions}
      columns={columns}
      loading={isFetching}
      totalCount={data?.count ?? 0}
      currentPage={page}
      pageSize={pageSize}
      searchable={false}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setPage(1);
      }}
      onRowClick={(row: RegisterSession) =>
        navigate(`/register/sessions/${row.id}`)
      }
      emptyMessage="Aucune session de caisse"
    />
  );
};

export default SessionsList;
